import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/i18n/useLanguage";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from "@/i18n";

const SITE_URL = "https://ailabbet.com";

interface LocalizedString {
  en: string;
  sv: string;
}

interface SeoHeadProps {
  /** Either a single string (same for all languages) or per-language object */
  title: string | LocalizedString;
  description: string | LocalizedString;
  /** Canonical path without language prefix, e.g. "/about" */
  path: string;
  type?: "website" | "article";
  noIndex?: boolean;
  jsonLd?: object | object[];
  /** Absolute URL for page-specific social preview image */
  image?: string;
}

const pick = (value: string | LocalizedString, lang: "en" | "sv"): string =>
  typeof value === "string" ? value : value[lang] ?? value.en;

const localizedUrl = (path: string, lang: string) => {
  const clean = path === "/" ? "" : path;
  return lang === DEFAULT_LANGUAGE ? `${SITE_URL}${clean || "/"}` : `${SITE_URL}/${lang}${clean}`;
};

/**
 * Per-route SEO head with hreflang alternates for EN/SV.
 */
const SeoHead = ({
  title,
  description,
  path,
  type = "website",
  noIndex,
  jsonLd,
  image,
}: SeoHeadProps) => {
  const { lang } = useLanguage();
  const resolvedTitle = pick(title, lang);
  const resolvedDescription = pick(description, lang);
  const url = localizedUrl(path, lang);
  const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={url} />

      {SUPPORTED_LANGUAGES.map((l) => (
        <link
          key={l}
          rel="alternate"
          hrefLang={l}
          href={localizedUrl(path, l)}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={localizedUrl(path, DEFAULT_LANGUAGE)} />

      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={lang === "sv" ? "sv_SE" : "en_US"} />
      {SUPPORTED_LANGUAGES.filter((l) => l !== lang).map((l) => (
        <meta key={l} property="og:locale:alternate" content={l === "sv" ? "sv_SE" : "en_US"} />
      ))}
      {image && <meta property="og:image" content={image} />}
      {image && <meta name="twitter:image" content={image} />}

      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />

      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {ldArray.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
};

export default SeoHead;
